import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Smiley, CurrencyCircleDollar, UsersThree } from '@phosphor-icons/react';

import { getPulseRoleTrend } from '@/api/pulse';
import { getInvoiceForecast, getMembershipHistory } from '@/api/billing';
import { listMembers } from '@/api/members';
import { StatCard } from '@/components/ui/stat-card';
import { Sparkline } from '@/components/ui/sparkline';
import { DeltaChip } from '@/components/ui/delta-chip';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoneyRounded } from '@/lib/money';
import { collapsePulseRounds } from '@/lib/pulse-trend';
import { MetricDetailDialog, type MetricKey } from '@/components/home/MetricDetailDialog';

// KpiRow — the three at-a-glance daily numbers: member satisfaction (eNPS +
// trend), money coming in (next 30 days), and paid members. Each tile owns its
// own query so one slow or failed source never blanks the row; a pending tile
// shows a compact skeleton.
//
// Tapping a tile opens MetricDetailDialog — the long view of that metric plus a
// link onward to the page that owns it. The tile answers "what is it now", the
// dialog answers "how has it gone"; the link to the full page moved inside the
// dialog so the tile has exactly one action.
export function KpiRow({ slug, token }: { slug: string; token: string | null }) {
  const [openMetric, setOpenMetric] = useState<MetricKey | null>(null);
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <EnpsTile slug={slug} token={token} onOpen={() => setOpenMetric('enps')} />
        <RevenueTile slug={slug} token={token} onOpen={() => setOpenMetric('money')} />
        <MembersTile slug={slug} token={token} onOpen={() => setOpenMetric('members')} />
      </div>
      {openMetric && (
        <MetricDetailDialog
          metric={openMetric}
          open
          onOpenChange={(next) => {
            if (!next) setOpenMetric(null);
          }}
          slug={slug}
          token={token}
        />
      )}
    </>
  );
}

function TileSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <Skeleton className="h-9 w-9 rounded-full" />
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-7 w-16" />
    </div>
  );
}

function EnpsTile({
  slug,
  token,
  onOpen,
}: {
  slug: string;
  token: string | null;
  onOpen: () => void;
}) {
  const { t } = useTranslation('common');
  const q = useQuery({
    queryKey: ['home-enps', slug],
    queryFn: () => getPulseRoleTrend(token, slug, 'all'),
    enabled: !!token && !!slug,
  });
  if (q.isLoading) return <TileSkeleton />;

  // Collapse the three role-campaigns of each round into one point before
  // anything is derived: otherwise the delta chip compares two ROLES from the
  // same round rather than this round against the previous one.
  const points = collapsePulseRounds(q.data?.points ?? []).map((p) => ({
    at: Date.parse(p.at),
    v: p.enps ?? null,
  }));
  const present = points.filter((p): p is { at: number; v: number } => p.v != null);
  const latest = present.length ? present[present.length - 1]!.v : null;
  const prior = present.length > 1 ? present[present.length - 2]!.v : null;
  const delta = latest != null && prior != null ? latest - prior : null;

  return (
    <StatCard
      icon={<Smiley size={18} weight="bold" aria-hidden />}
      accent="lime"
      label={t('home.kpi.enps')}
      value={latest != null ? Math.round(latest) : '—'}
      trailing={delta != null ? <DeltaChip delta={Math.round(delta)} /> : undefined}
      onClick={onOpen}
      ariaLabel={t('home.kpi.detail.open', { metric: t('home.kpi.enps') })}
    >
      {present.length >= 2 && <Sparkline points={points} color="var(--lime)" width={140} height={28} />}
    </StatCard>
  );
}

function RevenueTile({
  slug,
  token,
  onOpen,
}: {
  slug: string;
  token: string | null;
  onOpen: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const q = useQuery({
    queryKey: ['home-forecast', slug],
    queryFn: () => getInvoiceForecast(token, slug, 30),
    enabled: !!token && !!slug,
  });
  if (q.isLoading) return <TileSkeleton />;
  const fc = q.data;
  const value = fc ? formatMoneyRounded(fc.incoming_minor, fc.currency, i18n.language) : '—';
  const overdue =
    fc && fc.overdue_minor > 0
      ? t('home.kpi.overdue', {
          amount: formatMoneyRounded(fc.overdue_minor, fc.currency, i18n.language),
        })
      : undefined;

  return (
    <StatCard
      icon={<CurrencyCircleDollar size={18} weight="bold" aria-hidden />}
      accent="aqua"
      label={t('home.kpi.incoming')}
      value={value}
      sub={overdue}
      onClick={onOpen}
      ariaLabel={t('home.kpi.detail.open', { metric: t('home.kpi.incoming') })}
    />
  );
}

// MembersTile counts PAID members — those who have settled the membership fee
// for the club's current fiscal year — with the roster size as the sub-line.
// A roster row is not a member who has paid, and the club's own definition of
// "member" is the paying one.
//
// Fallback: a club with no membership-fee invoice type configured would show a
// hard 0 for a reason that has nothing to do with its members, so that case
// falls back to the roster count under the plain "members" label. Same for a
// failed billing query — the roster number is always the safe thing to show.
function MembersTile({
  slug,
  token,
  onOpen,
}: {
  slug: string;
  token: string | null;
  onOpen: () => void;
}) {
  const { t } = useTranslation('common');
  const roster = useQuery({
    queryKey: ['home-members-count', slug],
    queryFn: () => listMembers(token, slug, { limit: 1 }),
    enabled: !!token && !!slug,
  });
  const membership = useQuery({
    queryKey: ['home-membership', slug],
    queryFn: () => getMembershipHistory(token, slug, 5),
    enabled: !!token && !!slug,
  });
  if (roster.isLoading) return <TileSkeleton />;

  const rosterTotal = roster.data?.total ?? null;
  const mh = membership.data;
  const paidYear = mh?.years.find((y) => y.fiscal_year === mh.current_fiscal_year);
  const showPaid = !!mh?.configured && paidYear != null;

  return (
    <StatCard
      icon={<UsersThree size={18} weight="bold" aria-hidden />}
      accent="pink"
      label={showPaid ? t('home.kpi.paidMembers') : t('home.kpi.members')}
      value={showPaid ? paidYear.paid_members : (rosterTotal ?? '—')}
      sub={
        showPaid && rosterTotal != null
          ? t('home.kpi.ofRoster', { count: rosterTotal })
          : undefined
      }
      onClick={onOpen}
      ariaLabel={t('home.kpi.detail.open', {
        metric: showPaid ? t('home.kpi.paidMembers') : t('home.kpi.members'),
      })}
    />
  );
}
